import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { formatDateTime, cn } from '../../lib/utils';
import { Plus, AlertTriangle, FileText, Clock, User, Home, Eye, ImagePlus, X, Image } from 'lucide-react';
import { toast } from 'sonner';

// NDIS Reportable Incident Categories
const INCIDENT_CATEGORIES = [
  { value: 'death', label: 'Death of a person with disability', reportable: true },
  { value: 'serious_injury', label: 'Serious injury', reportable: true },
  { value: 'abuse_neglect', label: 'Abuse or neglect', reportable: true },
  { value: 'unlawful_sexual_contact', label: 'Unlawful sexual or physical contact', reportable: true },
  { value: 'sexual_misconduct', label: 'Sexual misconduct', reportable: true },
  { value: 'unauthorised_restrictive', label: 'Unauthorised use of restrictive practice', reportable: true },
  { value: 'medication_error', label: 'Medication error', reportable: false },
  { value: 'fall', label: 'Fall', reportable: false },
  { value: 'behaviour_incident', label: 'Behaviour of concern', reportable: false },
  { value: 'property_damage', label: 'Property damage', reportable: false },
  { value: 'near_miss', label: 'Near miss', reportable: false },
  { value: 'other', label: 'Other', reportable: false },
];

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low - Minor incident, no harm', color: 'bg-blue-500' },
  { value: 'medium', label: 'Medium - Some harm or potential for harm', color: 'bg-amber-500' },
  { value: 'high', label: 'High - Significant harm', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical - Life threatening or death', color: 'bg-red-500' },
];

const WorkerIncidentsPage = () => {
  const { api, user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [incidentImages, setIncidentImages] = useState([]);
  
  const [newIncident, setNewIncident] = useState({
    participant_id: '',
    house_id: '',
    category: '',
    severity: 'low',
    description: '',
    immediate_actions: '',
    injuries: '',
    witnesses: '',
    notifications: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [incidentsRes, participantsRes, housesRes] = await Promise.all([
        api.get('/incidents', { params: { reporter_id: user.id } }),
        api.get('/participants'),
        api.get('/houses')
      ]);
      setIncidents(incidentsRes.data);
      setParticipants(participantsRes.data);
      setHouses(housesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIncident = async (asDraft = true) => {
    if (!newIncident.participant_id || !newIncident.house_id || !newIncident.category || !newIncident.description) {
      toast.error('Please fill in all required fields (Participant, House, Category, Description)');
      return;
    }

    try {
      const incidentData = {
        participant_id: newIncident.participant_id,
        house_id: newIncident.house_id,
        category: newIncident.category,
        severity: newIncident.severity,
        description: newIncident.description,
        immediate_actions: newIncident.immediate_actions || null,
        injuries: newIncident.injuries || null,
        witnesses: newIncident.witnesses ? newIncident.witnesses.split(',').map(w => w.trim()).filter(w => w) : [],
        notifications: newIncident.notifications ? newIncident.notifications.split(',').map(n => n.trim()).filter(n => n) : []
      };
      
      const response = await api.post('/incidents', incidentData);
      
      if (selectedImages.length > 0 && response.data?.id) {
        await uploadImagesToIncident(response.data.id);
      }
      toast.success('Incident report submitted successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Incident creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create incident report');
    }
  };

  const handleEditDraft = (incident) => {
    setEditingIncident({
      ...incident,
      participant_id: incident.participant_id || "",
      house_id: incident.house_id || "",
      category: incident.category || "",
      severity: incident.severity || "low",
      description: incident.description || "",
      immediate_actions: incident.immediate_actions || "",
      injuries: incident.injuries || "",
      witnesses: incident.witnesses || "",
      notifications: incident.notifications || ""
    });
    setEditMode(true);
    setCreateDialogOpen(true);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadImagesToIncident = async (incidentId) => {
    if (selectedImages.length === 0) return;
    setUploadingImages(true);
    try {
      for (const img of selectedImages) {
        const formData = new FormData();
        formData.append("file", img.file);
        await api.post(`/incidents/${incidentId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(`${selectedImages.length} image(s) uploaded`);
      setSelectedImages([]);
    } catch (error) {
      toast.error("Failed to upload some images");
    } finally {
      setUploadingImages(false);
    }
  };

  const fetchIncidentImages = async (incidentId) => {
    try {
      const res = await api.get(`/incidents/${incidentId}/images`);
      setIncidentImages(res.data || []);
    } catch (e) {
      setIncidentImages([]);
    }
  };

  const handleSaveEdit = async (submit = false) => {
    if (!editingIncident) return;
    try {
      await api.put(`/incidents/${editingIncident.id}`, {
        participant_id: editingIncident.participant_id,
        house_id: editingIncident.house_id,
        category: editingIncident.category,
        severity: editingIncident.severity,
        description: editingIncident.description,
        immediate_actions: editingIncident.immediate_actions,
        injuries: editingIncident.injuries,
        witnesses: editingIncident.witnesses,
        notifications: editingIncident.notifications
      });
      
      if (submit) {
        await api.put(`/incidents/${editingIncident.id}/submit`);
        toast.success("Incident report submitted successfully");
      } else {
        toast.success("Draft saved successfully");
      }
      
      setCreateDialogOpen(false);
      setEditMode(false);
      setEditingIncident(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save incident");
    }
  };

  const handleSubmitDraft = async (incidentId) => {
    try {
      await api.put(`/incidents/${incidentId}/submit`);
      toast.success('Incident report submitted for review');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit incident');
    }
  };

  const resetForm = () => {
    setNewIncident({
      participant_id: '',
      house_id: '',
      category: '',
      severity: 'low',
      description: '',
      immediate_actions: '',
      injuries: '',
      witnesses: '',
      notifications: ''
    });
    setSelectedImages([]);
  };

  const formData = editMode ? editingIncident : newIncident;
  const setFormData = editMode 
    ? (data) => setEditingIncident(typeof data === "function" ? data(editingIncident) : data)
    : (data) => setNewIncident(typeof data === "function" ? data(newIncident) : data);

  const openCreateDialog = () => {
    resetForm();
    setEditMode(false);
    setEditingIncident(null);
    setCreateDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500';
      case 'submitted': return 'bg-blue-500';
      case 'under_review': return 'bg-amber-500';
      case 'approved': return 'bg-green-500';
      case 'published': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const isReportable = (category) => {
    return INCIDENT_CATEGORIES.find(c => c.value === category)?.reportable || false;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="worker-incidents-page">
      <PageHeader 
        title="Incident Reports"
        description="Report and track incidents following NDIS requirements"
        actions={
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} data-testid="create-incident-btn">
                <Plus className="h-4 w-4 mr-2" />
                Report Incident
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  {editMode ? "Edit Draft Incident" : "New Incident Report"}
                </DialogTitle>
                <DialogDescription>
                  Complete this form as accurately as possible. Fields marked with * are required.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Basic Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Participant *</Label>
                      <Select 
                        value={formData?.participant_id || ""} 
                        onValueChange={(v) => {
                          const participant = participants.find(p => p.id === v);
                          setNewIncident({
                            ...newIncident, 
                            participant_id: v,
                            house_id: participant?.house_id || ''
                          });
                        }}
                      >
                        <SelectTrigger data-testid="incident-participant">
                          <SelectValue placeholder="Select participant" />
                        </SelectTrigger>
                        <SelectContent>
                          {participants.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.first_name} {p.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>House</Label>
                      <Select 
                        value={formData?.house_id} 
                        onValueChange={(v) => setFormData({...formData, house_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select house" />
                        </SelectTrigger>
                        <SelectContent>
                          {houses.map(h => (
                            <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Incident Classification */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Incident Classification</h3>
                  
                  <div className="space-y-2">
                    <Label>Incident Category *</Label>
                    <Select 
                      value={formData?.category} 
                      onValueChange={(v) => setFormData({...formData, category: v})}
                    >
                      <SelectTrigger data-testid="incident-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1 text-xs font-semibold text-red-500">NDIS Reportable Incidents</div>
                        {INCIDENT_CATEGORIES.filter(c => c.reportable).map(c => (
                          <SelectItem key={c.value} value={c.value}>
                            <span className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-red-500"></span>
                              {c.label}
                            </span>
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">Other Incidents</div>
                        {INCIDENT_CATEGORIES.filter(c => !c.reportable).map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newIncident.category && isReportable(newIncident.category) && (
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm">
                        <strong className="text-red-500">NDIS Reportable Incident</strong>
                        <p className="text-muted-foreground mt-1">
                          This incident type must be reported to the NDIS Commission within 24 hours of becoming aware.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Severity Level *</Label>
                    <Select 
                      value={formData?.severity} 
                      onValueChange={(v) => setFormData({...formData, severity: v})}
                    >
                      <SelectTrigger data-testid="incident-severity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SEVERITY_LEVELS.map(s => (
                          <SelectItem key={s.value} value={s.value}>
                            <span className="flex items-center gap-2">
                              <span className={cn("h-2 w-2 rounded-full", s.color)}></span>
                              {s.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Incident Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Incident Details</h3>
                  
                  <div className="space-y-2">
                    <Label>Description of Incident *</Label>
                    <Textarea 
                      placeholder="Describe what happened in detail. Include who was involved, what occurred, and any relevant circumstances..."
                      value={formData?.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={4}
                      data-testid="incident-description"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Immediate Actions Taken</Label>
                    <Textarea 
                      placeholder="What actions did you take immediately after the incident?"
                      value={formData?.immediate_actions}
                      onChange={(e) => setFormData({...formData, immediate_actions: e.target.value})}
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Injuries Sustained (if any)</Label>
                    <Textarea 
                      placeholder="Describe any injuries to the participant or others..."
                      value={formData?.injuries}
                      onChange={(e) => setFormData({...formData, injuries: e.target.value})}
                      rows={2}
                    />
                  </div>
                </div>

                {/* Witnesses & Notifications */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Witnesses & Notifications</h3>
                  
                  <div className="space-y-2">
                    <Label>Witnesses</Label>
                    <Input 
                      placeholder="Names of any witnesses to the incident"
                      value={formData?.witnesses}
                      onChange={(e) => setFormData({...formData, witnesses: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notifications Made</Label>
                    <Input 
                      placeholder="Who was notified? (e.g., Family, Manager, Emergency services)"
                      value={formData?.notifications}
                      onChange={(e) => setFormData({...formData, notifications: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="space-y-2 border-t pt-4">
                <Label className="flex items-center gap-2">
                  <ImagePlus className="h-4 w-4" />
                  Add Photos
                </Label>
                <div className="flex flex-wrap gap-2">
                  {selectedImages.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20">
                      <img src={img.preview} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                    <ImagePlus className="h-6 w-6 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Add</span>
                    <input type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" />
                  </label>
                </div>
                <p className="text-xs text-muted-foreground">Photos help document the incident</p>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => { setCreateDialogOpen(false); setEditMode(false); setEditingIncident(null); }}>
                  Cancel
                </Button>
                {editMode ? (
                  <>
                    <Button variant="secondary" onClick={() => handleSaveEdit(false)} data-testid="save-edit-btn">
                      Save Draft
                    </Button>
                    <Button onClick={() => handleSaveEdit(true)} data-testid="submit-edit-btn">
                      Submit Report
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" onClick={() => handleCreateIncident(true)} data-testid="save-draft-btn">
                      Save as Draft
                    </Button>
                    <Button onClick={() => handleCreateIncident(false)} data-testid="submit-incident-btn">
                      Submit Report
                    </Button>
                  </>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* My Incident Reports */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-lg">My Incident Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : incidents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No incident reports found</p>
              <p className="text-sm mt-2">Click &quot;Report Incident&quot; to create a new report</p>
            </div>
          ) : (
            <div className="space-y-3">
              {incidents.map(incident => (
                <div 
                  key={incident.id}
                  className="p-4 rounded-lg border border-border/40 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{incident.participant_name}</span>
                        <Badge className={cn("text-xs", getStatusColor(incident.status))}>
                          {incident.status?.replace('_', ' ')}
                        </Badge>
                        {isReportable(incident.category) && (
                          <Badge variant="outline" className="text-xs border-red-500 text-red-500">
                            NDIS Reportable
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {INCIDENT_CATEGORIES.find(c => c.value === incident.category)?.label || incident.category}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDateTime(incident.date_time || incident.created_at)}
                        </span>
                        {incident.house_name && (
                          <span className="flex items-center gap-1">
                            <Home className="h-3 w-3" />
                            {incident.house_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {incident.status === 'draft' && (
                        <>
                          <Button 
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditDraft(incident)}
                            data-testid={`edit-draft-${incident.id}`}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => handleSubmitDraft(incident.id)}
                            data-testid={`submit-draft-${incident.id}`}
                          >
                            Submit
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setSelectedIncident(incident);
                          fetchIncidentImages(incident.id);
                          setViewDialogOpen(true);
                        }}
                        data-testid={`view-incident-${incident.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Incident Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Incident Report Details</DialogTitle>
          </DialogHeader>
          {selectedIncident && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <Badge className={cn(getStatusColor(selectedIncident.status))}>
                  {selectedIncident.status?.replace('_', ' ')}
                </Badge>
                {isReportable(selectedIncident.category) && (
                  <Badge variant="outline" className="border-red-500 text-red-500">
                    NDIS Reportable
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Participant:</span>
                  <p className="font-medium">{selectedIncident.participant_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">House:</span>
                  <p className="font-medium">{selectedIncident.house_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Date/Time:</span>
                  <p className="font-medium">{formatDateTime(selectedIncident.date_time || selectedIncident.created_at)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Severity:</span>
                  <p className="font-medium capitalize">{selectedIncident.severity}</p>
                </div>
              </div>

              <div>
                <span className="text-muted-foreground text-sm">Category:</span>
                <p className="font-medium">
                  {INCIDENT_CATEGORIES.find(c => c.value === selectedIncident.category)?.label || selectedIncident.category}
                </p>
              </div>

              <div>
                <span className="text-muted-foreground text-sm">Description:</span>
                <p className="mt-1 p-3 rounded bg-muted/30">{selectedIncident.description}</p>
              </div>

              {selectedIncident.immediate_actions && (
                <div>
                  <span className="text-muted-foreground text-sm">Immediate Actions:</span>
                  <p className="mt-1 p-3 rounded bg-muted/30">{selectedIncident.immediate_actions}</p>
                </div>
              )}

              {selectedIncident.admin_notes && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <span className="text-sm font-medium text-blue-500">Reviewer Notes:</span>
                  <p className="mt-1">{selectedIncident.admin_notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Incident Images */}
          {incidentImages.length > 0 && (
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Attached Photos ({incidentImages.length})
              </Label>
              <div className="flex flex-wrap gap-2">
                {incidentImages.map((img) => (
                  <a
                    key={img.id}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/incidents/images/${img.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-24 h-24 rounded-lg overflow-hidden border hover:border-primary transition-colors"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/incidents/images/${img.id}`}
                      alt={img.filename}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerIncidentsPage;
